import React, { useState, useRef, useEffect } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Loader2, UploadCloud, Wand2, Check, ArrowRight, AlertTriangle, FileQuestion, Trash2, BookOpen, Link2, Plus, FileInput, FileOutput, FolderUp } from "lucide-react";

const STATUS_META = {
  ready: { label: "Prête", cls: "bg-signal-success/15 text-signal-success" },
  unknown: { label: "À définir", cls: "bg-signal-processing/15 text-signal-processing" },
  skip: { label: "Ignorée", cls: "bg-white/5 text-zinc-500" },
};

export const SmartImportWizard = ({ services, onImported }) => {
  const [file, setFile] = useState(null);
  const [url, setUrl] = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [defs, setDefs] = useState({});
  const [brand, setBrand] = useState("");
  const [ecu, setEcu] = useState("");
  const [ecuByMod, setEcuByMod] = useState({});
  const [committing, setCommitting] = useState(false);
  const fileRef = useRef();
  const folderRef = useRef();
  const [folderProgress, setFolderProgress] = useState(null); // { sent, total }
  const svcName = (id) => services.find((s) => s.id === id)?.name || id;

  // Mirror backend `_is_import_file`: accept ECU binaries incl. WinOLS label extensions, reject docs.
  const REL_OK = (n) => {
    const l = (n || "").toLowerCase();
    if (l === "thumbs.db" || l.endsWith(".ds_store")) return false;
    const SKIP = [".pdf", ".txt", ".csv", ".doc", ".docx", ".xls", ".xlsx", ".ppt", ".pptx", ".png", ".jpg", ".jpeg", ".gif", ".bmp", ".webp", ".svg", ".ico", ".zip", ".rar", ".7z", ".gz", ".tar", ".log", ".xml", ".json", ".html", ".htm", ".md", ".ini", ".cfg", ".exe", ".dll", ".db", ".sqlite", ".mp3", ".mp4", ".mov", ".avi"];
    const IMPORT = [".bin", ".ori", ".mod", ".hex", ".dat", ".frf", ".kp"];
    const KW = ["ori", "orig", "original", "stock", "std", "read", "backup", "org", "mod", "modified", "tuned", "stage", "remap", "stg", "tun", "map", "egr", "dpf", "fap", "adblue", "o2", "dtc"];
    const dot = l.lastIndexOf(".");
    if (dot < 0) return true;
    const suf = l.slice(dot);
    if (SKIP.includes(suf)) return false;
    if (IMPORT.includes(suf)) return true;
    const label = suf.slice(1);
    if (!label) return true;
    if (KW.some((k) => label.includes(k))) return true;
    if (/^(stage|stg|st)\d*$/.test(label)) return true;
    if (/^\d+$/.test(label)) return true;
    if (/^[a-z0-9]+$/.test(label) && label.length <= 12) return true;
    return false;
  };

  const analyzeFolder = async (fileList) => {
    const all = Array.from(fileList || []).filter((f) => REL_OK(f.name) && f.size > 0);
    if (all.length === 0) return toast.error("Aucun fichier ECU valide trouvé dans le dossier");
    setAnalyzing(true);
    setFolderProgress({ sent: 0, total: all.length });
    try {
      const { data: startData } = await api.post("/admin/solutions/folder_import/start", { services: [], brand: "", ecu: "" });
      const jobId = startData.job_id;
      const CHUNK = 8;
      for (let i = 0; i < all.length; i += CHUNK) {
        const slice = all.slice(i, i + CHUNK);
        const fd = new FormData();
        const paths = [];
        slice.forEach((f) => { fd.append("files", f); paths.push(f.webkitRelativePath || f.name); });
        fd.append("paths", JSON.stringify(paths));
        await api.post(`/admin/solutions/folder_import/${jobId}/batch`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        setFolderProgress({ sent: Math.min(i + CHUNK, all.length), total: all.length });
      }
      const { data } = await api.post(`/admin/solutions/analyze_import/from_job/${jobId}`);
      setAnalysis(data);
      const init = {};
      data.unknown_keywords.forEach((k) => (init[k] = { services: [], ignore: false }));
      setDefs(init);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur d'analyse du dossier"); }
    finally { setAnalyzing(false); setFolderProgress(null); }
  };


  const analyze = async () => {
    if (!file && !url.trim()) return toast.error("Ajoutez un .zip ou une URL");
    setAnalyzing(true);
    try {
      const fd = new FormData();
      if (file) fd.append("file", file);
      if (url.trim()) fd.append("source_url", url.trim());
      const { data } = await api.post("/admin/solutions/analyze_import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAnalysis(data);
      const init = {};
      data.unknown_keywords.forEach((k) => (init[k] = { services: [], ignore: false }));
      setDefs(init);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur d'analyse"); }
    finally { setAnalyzing(false); }
  };

  const reresolve = async () => {
    const { data } = await api.get(`/admin/solutions/analysis/${analysis.analysis_id}`);
    setAnalysis(data);
    setDefs((prev) => {
      const init = {};
      data.unknown_keywords.forEach((k) => (init[k] = prev[k] || { services: [], ignore: false }));
      return init;
    });
  };

  const toggleDefSvc = (kw, sid) => setDefs((d) => {
    const cur = d[kw] || { services: [], ignore: false };
    const on = cur.services.includes(sid);
    return { ...d, [kw]: { ignore: false, services: on ? cur.services.filter((x) => x !== sid) : [...cur.services, sid] } };
  });
  const toggleDefIgnore = (kw) => setDefs((d) => ({ ...d, [kw]: { services: [], ignore: !(d[kw]?.ignore) } }));

  const saveDef = async (kw) => {
    const def = defs[kw] || { services: [], ignore: false };
    if (!def.ignore && def.services.length === 0) return toast.error("Choisissez une solution ou « Ignorer »");
    try {
      await api.post("/admin/keyword_map", { keyword: kw, services: def.services, ignore: def.ignore });
      toast.success(`« ${kw} » enregistré`);
      await reresolve();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const commit = async () => {
    setCommitting(true);
    try {
      const { data } = await api.post("/admin/solutions/commit_import", { analysis_id: analysis.analysis_id, brand, ecu, ecu_by_mod: ecuByMod });
      let done = false, tries = 0;
      while (!done && tries < 50) {
        await new Promise((r) => setTimeout(r, 1200));
        const { data: jobs } = await api.get("/admin/solutions/import_jobs");
        const j = jobs.find((x) => x.id === data.job_id);
        if (j && (j.status === "done" || j.status === "failed")) {
          done = true;
          if (j.status === "done") toast.success(`${j.imported} solution(s) importée(s) · ${j.skipped} ignorée(s)`);
          else toast.error("Import échoué");
        }
        tries++;
      }
      setAnalysis(null); setFile(null); setUrl(""); setDefs({}); setBrand(""); setEcu(""); setEcuByMod({});
      onImported?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setCommitting(false); }
  };

  const inputCls = "w-full bg-white/5 border border-white/10 rounded-sm px-3 py-2.5 text-sm focus:border-aurora-cyan focus:outline-none";

  return (
    <div className="glass rounded-md p-6 mb-6" data-testid="smart-import">
      <div className="flex items-center gap-2 mb-1">
        <Wand2 className="w-5 h-5 text-aurora-purple" />
        <h3 className="font-display text-lg font-semibold uppercase tracking-tight">Import intelligent</h3>
      </div>
      <p className="font-mono text-[10px] text-zinc-500 mb-5 leading-snug">
        Je lis le nom des fichiers modifiés (ex. <span className="text-aurora-cyan">stage_adblue_scr.bin</span>) et déduis la/les solution(s). Pour un mot-clé inconnu, tu me dis ce qu'il signifie — je le mémorise pour la suite.
      </p>

      {!analysis ? (
        <div className="space-y-3">
          <input ref={fileRef} type="file" accept=".zip,.rar,.7z" className="hidden" data-testid="smart-file-input" onChange={(e) => setFile(e.target.files[0])} />
          <button onClick={() => fileRef.current?.click()} className="w-full flex items-center gap-3 p-3 rounded-sm border border-dashed border-aurora-purple/40 hover:border-aurora-purple transition-colors">
            <UploadCloud className="w-5 h-5 text-aurora-purple shrink-0" />
            <span className="font-mono text-xs text-zinc-300 truncate">{file ? file.name : "Archive .zip / .rar / .7z de paires (original + modifié)"}</span>
          </button>
          <input ref={folderRef} type="file" webkitdirectory="" directory="" multiple className="hidden" data-testid="smart-folder-input"
            onChange={(e) => { const f = e.target.files; e.target.value = ""; if (f && f.length) analyzeFolder(f); }} />
          <button data-testid="smart-folder-btn" onClick={() => folderRef.current?.click()} disabled={analyzing}
            className="w-full flex items-center gap-3 p-3 rounded-sm border border-dashed border-aurora-cyan/40 hover:border-aurora-cyan transition-colors disabled:opacity-60">
            <FolderUp className="w-5 h-5 text-aurora-cyan shrink-0" />
            <span className="font-mono text-xs text-zinc-300 truncate">
              {folderProgress ? `Envoi ${folderProgress.sent}/${folderProgress.total} fichiers…` : "…ou choisir un dossier (avec sous-dossiers)"}
            </span>
            {folderProgress && <Loader2 className="w-4 h-4 animate-spin text-aurora-cyan ml-auto shrink-0" />}
          </button>
          <div className="flex items-center gap-2">
            <Link2 className="w-4 h-4 text-zinc-500 shrink-0" />
            <input data-testid="smart-url-input" className={inputCls} placeholder="ou un lien de téléchargement .zip" value={url} onChange={(e) => setUrl(e.target.value)} />
          </div>
          <button data-testid="smart-analyze-btn" onClick={analyze} disabled={analyzing} className="flex items-center gap-2 px-6 py-3 bg-aurora-purple text-white font-display font-bold uppercase tracking-wider rounded-sm hover:bg-purple-500 transition-colors disabled:opacity-60">
            {analyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />} Analyser
          </button>
        </div>
      ) : (
        <div className="space-y-5">
          <div className="flex flex-wrap items-center gap-3 font-mono text-[10px] uppercase tracking-widest" data-testid="smart-summary">
            <span className="text-zinc-400">{analysis.summary.total} paires</span>
            <span className="text-signal-success">{analysis.summary.ready} prêtes</span>
            <span className="text-signal-processing">{analysis.summary.unknown_pairs} à définir</span>
            <span className="text-zinc-500">{analysis.summary.skip} ignorées</span>
            <button onClick={() => { setAnalysis(null); setDefs({}); }} className="ml-auto text-zinc-500 hover:text-white underline">recommencer</button>
          </div>

          {analysis.unknown_keywords.length > 0 && (
            <div className="border border-signal-processing/30 rounded-sm p-4 bg-signal-processing/5">
              <div className="flex items-center gap-2 mb-3">
                <FileQuestion className="w-4 h-4 text-signal-processing" />
                <span className="font-display uppercase tracking-tight text-sm">Mots-clés à définir ({analysis.unknown_keywords.length})</span>
              </div>
              <div className="space-y-3">
                {analysis.unknown_keywords.map((kw) => {
                  const def = defs[kw] || { services: [], ignore: false };
                  return (
                    <div key={kw} data-testid={`unknown-kw-${kw}`} className="bg-black/30 border border-white/10 rounded-sm p-3">
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <span className="font-mono text-sm text-aurora-cyan">« {kw} »</span>
                        <button data-testid={`save-def-${kw}`} onClick={() => saveDef(kw)} className="flex items-center gap-1.5 px-3 py-1.5 bg-aurora-cyan text-black font-mono text-[10px] uppercase tracking-widest rounded-sm hover:bg-cyan-300 transition-colors">
                          <Check className="w-3.5 h-3.5" /> Enregistrer
                        </button>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {services.map((s) => {
                          const on = def.services.includes(s.id);
                          return (
                            <button key={s.id} data-testid={`def-svc-${kw}-${s.id}`} onClick={() => toggleDefSvc(kw, s.id)}
                              className={`px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest rounded-sm border transition-colors ${on ? "border-aurora-cyan text-aurora-cyan bg-aurora-cyan/10" : "border-white/10 text-zinc-400 hover:border-white/30"}`}>
                              {s.name}
                            </button>
                          );
                        })}
                        <button data-testid={`def-ignore-${kw}`} onClick={() => toggleDefIgnore(kw)}
                          className={`px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest rounded-sm border transition-colors ${def.ignore ? "border-signal-error text-signal-error bg-signal-error/10" : "border-white/10 text-zinc-500 hover:border-white/30"}`}>
                          Ignorer
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Aperçu des paires <span className="text-zinc-600 normal-case tracking-normal">· ECU détecté depuis le nom (modifiable)</span></div>
            <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
              {analysis.pairs.map((p, i) => {
                const m = STATUS_META[p.status];
                const ecuVal = ecuByMod[p.mod_name] ?? p.ecu ?? "";
                return (
                  <div key={i} data-testid={`pair-row-${i}`} className="flex items-center gap-3 bg-black/20 border border-white/5 rounded-sm px-3 py-2">
                    <span className={`font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm shrink-0 ${m.cls}`}>{m.label}</span>
                    <span className="font-mono text-xs text-zinc-300 truncate flex-1 min-w-0">{p.mod_name}</span>
                    <input
                      data-testid={`pair-ecu-${i}`}
                      value={ecuVal}
                      onChange={(e) => setEcuByMod((prev) => ({ ...prev, [p.mod_name]: e.target.value }))}
                      placeholder="ECU"
                      title="Modèle ECU détecté depuis le nom de fichier (modifiable)"
                      className={`w-32 shrink-0 bg-black/40 border rounded-sm px-2 py-1 font-mono text-[11px] focus:outline-none focus:border-aurora-cyan ${ecuVal ? "border-aurora-cyan/40 text-aurora-cyan" : "border-white/10 text-zinc-500"}`}
                    />
                    <span className="font-mono text-[10px] text-signal-success truncate shrink-0 max-w-[28%]">{p.services.map(svcName).join(", ") || (p.unknown.length ? `? ${p.unknown.join(", ")}` : "—")}</span>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <input className={inputCls} placeholder="Marque (optionnel)" value={brand} onChange={(e) => setBrand(e.target.value)} data-testid="smart-brand" />
            <input className={inputCls} placeholder="ECU par défaut (repli)" value={ecu} onChange={(e) => setEcu(e.target.value)} data-testid="smart-ecu" />
          </div>
          {analysis.summary.unknown_pairs > 0 && (
            <p className="font-mono text-[10px] text-signal-processing flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" /> Les paires « à définir » sans solution seront ignorées.</p>
          )}
          <button data-testid="smart-commit-btn" onClick={commit} disabled={committing || analysis.summary.ready === 0}
            className="flex items-center gap-2 px-6 py-3 bg-signal-success text-black font-display font-bold uppercase tracking-wider rounded-sm hover:bg-green-400 transition-colors disabled:opacity-50">
            {committing ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />} Importer {analysis.summary.ready} paire(s)
          </button>
        </div>
      )}
    </div>
  );
};

export const KeywordDictionary = ({ services }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [kw, setKw] = useState("");
  const [type, setType] = useState("original");
  const [svc, setSvc] = useState([]);
  const [busy, setBusy] = useState(false);
  const svcName = (id) => services.find((s) => s.id === id)?.name || id;
  const load = () => api.get("/admin/keyword_map").then(({ data }) => setItems(data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);
  const remove = async (k) => { await api.delete(`/admin/keyword_map/${encodeURIComponent(k)}`); toast.success("Mot-clé supprimé"); load(); };
  const toggleSvc = (id) => setSvc((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const add = async () => {
    const key = kw.trim().toLowerCase();
    if (!key) return toast.error("Saisissez un mot-clé");
    let payload = { keyword: key };
    if (type === "original" || type === "modified") payload.role = type;
    else if (type === "ignore") payload.ignore = true;
    else { if (svc.length === 0) return toast.error("Sélectionnez au moins une solution"); payload.services = svc; }
    setBusy(true);
    try {
      await api.post("/admin/keyword_map", payload);
      toast.success(`Mot-clé « ${key} » enregistré`);
      setKw(""); setSvc([]);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const roleBadge = (it) => {
    if (it.role === "original") return <span className="font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm bg-aurora-purple/15 text-aurora-purple flex items-center gap-1"><FileInput className="w-3 h-3" /> Original</span>;
    if (it.role === "modified") return <span className="font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm bg-signal-success/15 text-signal-success flex items-center gap-1"><FileOutput className="w-3 h-3" /> Modifié</span>;
    if (it.ignore) return <span className="text-zinc-600 uppercase tracking-widest font-mono text-[10px]">ignoré</span>;
    return <span className="font-mono text-[10px] text-zinc-400 truncate">{(it.services || []).map(svcName).join(", ")}</span>;
  };

  const TYPES = [
    { id: "original", label: "Fichier original", icon: FileInput },
    { id: "modified", label: "Fichier modifié", icon: FileOutput },
    { id: "service", label: "Solution(s)", icon: Wand2 },
    { id: "ignore", label: "Ignoré", icon: Trash2 },
  ];

  return (
    <div className="glass rounded-md p-6 mb-6" data-testid="keyword-dictionary">
      <div className="flex items-center gap-2 mb-2">
        <BookOpen className="w-5 h-5 text-aurora-cyan" />
        <h3 className="font-display text-lg font-semibold uppercase tracking-tight">Dictionnaire de mots-clés</h3>
        <span className="ml-auto font-mono text-[10px] uppercase tracking-widest text-zinc-500">{items.length}</span>
      </div>
      <p className="font-mono text-[10px] text-zinc-500 mb-4 leading-snug">
        Associez un mot-clé de nom de fichier à une solution, ou marquez-le comme <b className="text-aurora-purple">fichier original</b> / <b className="text-signal-success">fichier modifié</b> pour aider l'appariement lors de l'import intelligent (ex. <span className="text-aurora-cyan">lecture</span>, <span className="text-aurora-cyan">kunde</span>, <span className="text-aurora-cyan">vergine</span> = original).
      </p>

      {/* Add form */}
      <div className="bg-black/20 border border-white/10 rounded-sm p-4 mb-5" data-testid="kw-add-form">
        <div className="flex flex-wrap gap-2 mb-3">
          {TYPES.map((tp) => (
            <button key={tp.id} data-testid={`kw-type-${tp.id}`} onClick={() => setType(tp.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest rounded-sm border transition-colors ${type === tp.id ? "border-aurora-cyan text-aurora-cyan bg-aurora-cyan/10" : "border-white/10 text-zinc-400 hover:border-white/30"}`}>
              <tp.icon className="w-3 h-3" /> {tp.label}
            </button>
          ))}
        </div>
        {type === "service" && (
          <div className="flex flex-wrap gap-2 mb-3">
            {services.map((s) => (
              <button key={s.id} data-testid={`kw-svc-${s.id}`} onClick={() => toggleSvc(s.id)}
                className={`px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest rounded-sm border transition-colors ${svc.includes(s.id) ? "border-aurora-cyan text-aurora-cyan bg-aurora-cyan/10" : "border-white/10 text-zinc-400 hover:border-white/30"}`}>
                {s.name}
              </button>
            ))}
          </div>
        )}
        <div className="flex gap-2">
          <input data-testid="kw-input" value={kw} onChange={(e) => setKw(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()}
            placeholder="ex. lecture, kunde, vergine..." className="flex-1 bg-black/50 border border-white/10 text-white px-3 py-2 rounded-sm text-sm focus:border-aurora-cyan focus:outline-none placeholder:text-zinc-600" />
          <button data-testid="kw-add-btn" onClick={add} disabled={busy}
            className="flex items-center gap-1.5 px-4 py-2 bg-aurora-cyan text-black font-display font-bold uppercase tracking-wider text-xs rounded-sm hover:bg-cyan-300 transition-colors shadow-glow disabled:opacity-60">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Ajouter
          </button>
        </div>
      </div>

      {loading ? <div className="font-mono text-xs text-zinc-500 py-6 text-center">Chargement...</div> : (
        <div className="grid sm:grid-cols-2 gap-2">
          {items.map((it) => (
            <div key={it.keyword} data-testid={`kw-${it.keyword}`} className="flex items-center gap-2 bg-black/20 border border-white/5 rounded-sm px-3 py-2">
              <span className="font-mono text-xs text-aurora-cyan shrink-0">{it.keyword}</span>
              <span className="flex-1 flex justify-end items-center min-w-0">{roleBadge(it)}</span>
              <button onClick={() => remove(it.keyword)} className="text-zinc-600 hover:text-signal-error transition-colors shrink-0"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
